import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { StatCard } from "@/components/common";
import {
  LineChart, Line, XAxis, YAxis, ResponsiveContainer, Tooltip, CartesianGrid,
  BarChart, Bar,
} from "recharts";
import {
  AlertTriangle, RefreshCw, TrendingUp, TrendingDown, Users, Flame,
  ChevronRight, Trophy, Mails, MessageCircle, Loader2,
} from "lucide-react";

const PRESETS = [
  { key: "today", label: "Today", days: 0 },
  { key: "7d", label: "7 Days", days: 7 },
  { key: "30d", label: "30 Days", days: 30 },
  { key: "month", label: "This Month", days: -1 },
];

function rangeFor(preset) {
  const now = new Date();
  const to = now.toISOString();
  let from;
  if (preset === "today") from = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  else if (preset === "month") from = new Date(now.getFullYear(), now.getMonth(), 1);
  else { const d = preset === "7d" ? 7 : 30; from = new Date(now.getTime() - d * 864e5); }
  return { from: from.toISOString(), to };
}

export default function Dashboard() {
  const nav = useNavigate();
  const [preset, setPreset] = useState("30d");
  const [d, setD] = useState(null);
  const [loading, setLoading] = useState(true);
  const [sortBy, setSortBy] = useState("score");

  const load = useCallback(async () => {
    setLoading(true);
    const { from, to } = rangeFor(preset);
    try {
      const { data } = await api.get("/analytics/dashboard", { params: { from, to } });
      setD(data);
    } catch (e) { /* handled by empty state */ }
    setLoading(false);
  }, [preset]);
  useEffect(() => { load(); }, [load]);

  if (loading && !d) return <div className="flex justify-center py-20"><Loader2 className="h-6 w-6 animate-spin text-primary" /></div>;
  if (!d) return <p className="text-muted-foreground">No data available.</p>;

  const k = d.kpis;
  const emps = [...d.employees].sort((a, b) => b[sortBy] - a[sortBy]);
  const kpiCards = [
    ["Total Leads", k.total, "/admin/leads"],
    ["New", k.new, "/admin/leads?view=new"],
    ["Unassigned", k.unassigned, "/admin/leads?view=unassigned"],
    ["Interested", k.interested, "/admin/leads?status=interested"],
    ["Details Sent", k.details_sent, "/admin/leads?status=details-sent"],
    ["Webinar Ready", k.webinar_ready, "/admin/leads?status=webinar-ready"],
    ["Payment Done", k.payment_done, "/admin/leads?status=payment-done"],
    ["Booking Done", k.booking_done, "/admin/leads?status=booking-done"],
    ["Lost", k.lost, "/admin/leads?status=lost"],
    ["Follow-ups Due", d.followups.due, "/admin/leads?view=followups"],
  ];
  const maxFunnel = Math.max(...d.funnel.map((f) => f.count), 1);

  return (
    <div className="space-y-6 pb-10">
      {/* Header + date range */}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="font-heading text-3xl font-extrabold">Command Center</h1>
          <p className="text-muted-foreground text-sm mt-1">Updated {new Date(d.updated_at).toLocaleTimeString()} · last {d.range.days} day(s)</p>
        </div>
        <div className="flex items-center gap-2">
          <div className="flex rounded-lg border border-slate-800 bg-slate-900/60 p-1">
            {PRESETS.map((p) => (
              <button key={p.key} data-testid={`range-${p.key}`} onClick={() => setPreset(p.key)}
                className={`px-3 py-1.5 text-xs rounded-md transition-colors ${preset === p.key ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:text-foreground"}`}>{p.label}</button>
            ))}
          </div>
          <button onClick={load} data-testid="refresh-button" className="p-2 rounded-lg border border-slate-800 bg-slate-900/60 hover:border-slate-600"><RefreshCw className={`h-4 w-4 ${loading ? "animate-spin" : ""}`} /></button>
        </div>
      </div>

      {/* Action required */}
      {d.alerts.length > 0 && (
        <div className="rounded-xl border border-amber-500/30 bg-amber-500/5 p-4">
          <div className="flex items-center gap-2 text-amber-400 font-semibold text-sm mb-3"><AlertTriangle className="h-4 w-4" /> Action Required</div>
          <div className="flex flex-wrap gap-2">
            {d.alerts.map((a) => (
              <button key={a.type} data-testid={`alert-${a.type}`} onClick={() => nav(a.link)}
                className="flex items-center gap-2 rounded-lg bg-slate-900/60 border border-slate-700 px-3 py-2 text-sm hover:border-amber-500/40">
                {a.label} <ChevronRight className="h-3.5 w-3.5 text-muted-foreground" />
              </button>
            ))}
          </div>
        </div>
      )}

      {/* KPI grid */}
      <div>
        {k.change_pct !== null && (
          <div className={`inline-flex items-center gap-1 text-xs mb-2 ${k.change_pct >= 0 ? "text-emerald-400" : "text-red-400"}`}>
            {k.change_pct >= 0 ? <TrendingUp className="h-3.5 w-3.5" /> : <TrendingDown className="h-3.5 w-3.5" />}
            {k.change_pct >= 0 ? "+" : ""}{k.change_pct}% vs previous {d.range.days}d ({k.prev_total} leads)
          </div>
        )}
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
          {kpiCards.map(([label, val, link]) => (
            <button key={label} data-testid={`kpi-${label.replace(/\s+/g, "-").toLowerCase()}`} onClick={() => nav(link)}
              className="glass rounded-xl p-4 text-left hover:border-slate-600 transition-colors">
              <div className="text-2xl font-extrabold font-heading">{val}</div>
              <div className="text-xs text-muted-foreground mt-0.5">{label}</div>
            </button>
          ))}
        </div>
      </div>

      {/* Leads by status — full breakdown */}
      {k.status_breakdown?.length > 0 && (
        <div>
          <h2 className="text-lg font-semibold mb-2">Leads by Status</h2>
          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-3">
            {k.status_breakdown.map((s) => (
              <button key={s.slug} data-testid={`status-count-${s.slug}`} onClick={() => nav(`/admin/leads?status=${s.slug}`)}
                className="glass rounded-xl p-4 text-left hover:border-slate-600 transition-colors">
                <div className="text-2xl font-extrabold font-heading">{s.count}</div>
                <div className="text-xs text-muted-foreground mt-0.5 truncate">{s.name}</div>
              </button>
            ))}
          </div>
        </div>
      )}

      <div className="grid lg:grid-cols-3 gap-4">
        {/* Funnel */}
        <div className="glass rounded-xl p-5 lg:col-span-2">
          <h2 className="font-semibold mb-4">Conversion Funnel</h2>
          <div className="space-y-2.5">
            {d.funnel.map((f, i) => {
              const prev = i > 0 ? d.funnel[i - 1].count : f.count;
              const rate = prev > 0 ? Math.round(f.count / prev * 100) : 0;
              return (
                <div key={f.stage} data-testid={`funnel-${i}`}>
                  <div className="flex justify-between text-xs mb-1"><span>{f.stage}</span><span className="text-muted-foreground">{f.count}{i > 0 && ` · ${rate}%`}</span></div>
                  <div className="h-3 rounded-full bg-slate-800 overflow-hidden">
                    <div className="h-full bg-gradient-to-r from-primary/70 to-primary" style={{ width: `${f.count / maxFunnel * 100}%` }} />
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Temperature */}
        <div className="glass rounded-xl p-5">
          <h2 className="font-semibold mb-4 flex items-center gap-2"><Flame className="h-4 w-4 text-orange-400" /> Temperature</h2>
          <div className="space-y-3">
            {[["HOT", "text-orange-400"], ["WARM", "text-amber-400"], ["COLD", "text-blue-400"], ["UNCLASSIFIED", "text-slate-400"]].map(([t, c]) => (
              <button key={t} data-testid={`temp-${t}`} onClick={() => nav(`/admin/leads?temperature=${t}`)} className="w-full flex justify-between items-center hover:opacity-80">
                <span className={`text-sm ${c}`}>{t}</span><span className="font-bold">{d.temperature[t]}</span>
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Trend */}
      <div className="glass rounded-xl p-5">
        <h2 className="font-semibold mb-4">Lead Trend</h2>
        {d.trend.length === 0 ? <p className="text-sm text-muted-foreground text-center py-6">No leads in this period.</p> : (
          <ResponsiveContainer width="100%" height={220}>
            <LineChart data={d.trend}>
              <CartesianGrid strokeDasharray="3 3" stroke="#1e293b" />
              <XAxis dataKey="date" stroke="#64748b" fontSize={11} />
              <YAxis stroke="#64748b" fontSize={11} allowDecimals={false} />
              <Tooltip contentStyle={{ background: "#0f172a", border: "1px solid #334155", borderRadius: 8 }} />
              <Line type="monotone" dataKey="leads" stroke="#10b981" strokeWidth={2} dot={false} />
              <Line type="monotone" dataKey="interested" stroke="#f59e0b" strokeWidth={2} dot={false} />
              <Line type="monotone" dataKey="payment" stroke="#3b82f6" strokeWidth={2} dot={false} />
            </LineChart>
          </ResponsiveContainer>
        )}
      </div>

      {/* Employee performance */}
      <div className="glass rounded-xl p-5">
        <div className="flex items-center justify-between mb-4">
          <h2 className="font-semibold flex items-center gap-2"><Trophy className="h-4 w-4 text-amber-400" /> Employee Performance</h2>
          <select value={sortBy} onChange={(e) => setSortBy(e.target.value)} data-testid="emp-sort" className="bg-slate-900/60 border border-slate-700 rounded-md px-2 py-1 text-xs">
            <option value="score">Overall Score</option><option value="conv">Conversion %</option>
            <option value="int_rate">Interested %</option><option value="assigned">Leads</option>
            <option value="payment">Payment</option><option value="calls">Calls</option>
          </select>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm min-w-[640px]">
            <thead><tr className="text-xs text-muted-foreground border-b border-slate-800">
              {["Employee", "Leads", "Calls", "Interested", "Payment", "Booking", "F/up ✓/✗", "Conv%", "Score"].map((h) => <th key={h} className="text-left py-2 px-2 font-medium">{h}</th>)}
            </tr></thead>
            <tbody>
              {emps.map((e, i) => (
                <tr key={e.id} data-testid={`emp-row-${e.id}`} onClick={() => nav(`/admin/leads?assigned_employee_id=${e.id}`)} className="border-b border-slate-800/50 hover:bg-slate-800/30 cursor-pointer">
                  <td className="py-2.5 px-2 font-medium">{i === 0 && sortBy === "score" ? "🏆 " : ""}{e.name}</td>
                  <td className="px-2">{e.assigned}</td><td className="px-2">{e.calls}</td>
                  <td className="px-2 text-amber-400">{e.interested}</td><td className="px-2 text-emerald-400">{e.payment}</td>
                  <td className="px-2 text-emerald-400">{e.booking}</td>
                  <td className="px-2 text-muted-foreground">{e.fu_done}/{e.fu_missed}</td>
                  <td className="px-2 font-semibold">{e.conv}%</td>
                  <td className="px-2"><span className="rounded-full bg-primary/15 text-primary px-2 py-0.5 text-xs font-bold">{e.score}</span></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <p className="text-[11px] text-muted-foreground mt-2">Score = 60% conversion + 30% interested rate + 10% follow-up completion. Ranks outcomes, not call volume.</p>
      </div>

      {/* Sources + Campaigns */}
      <div className="grid lg:grid-cols-2 gap-4">
        {[["Lead Sources", d.sources, "source"], ["Campaigns", d.campaigns, "campaign"]].map(([title, rows, field]) => (
          <div key={title} className="glass rounded-xl p-5">
            <h2 className="font-semibold mb-3">{title}</h2>
            {rows.length === 0 ? <p className="text-sm text-muted-foreground py-4">No {field} attribution available.</p> : (
              <table className="w-full text-sm">
                <thead><tr className="text-xs text-muted-foreground border-b border-slate-800"><th className="text-left py-1.5 font-medium">{field}</th><th className="px-2">Leads</th><th className="px-2">Int.</th><th className="px-2">Pay</th><th className="px-2">Conv%</th></tr></thead>
                <tbody>
                  {rows.map((r) => (
                    <tr key={r.key} data-testid={`${field}-row`} onClick={() => nav(`/admin/leads?${field}=${encodeURIComponent(r.key)}`)} className="border-b border-slate-800/50 hover:bg-slate-800/30 cursor-pointer">
                      <td className="py-2 capitalize">{r.key}</td><td className="px-2 text-center">{r.leads}</td>
                      <td className="px-2 text-center text-amber-400">{r.interested}</td><td className="px-2 text-center text-emerald-400">{r.payment}</td>
                      <td className="px-2 text-center font-semibold">{r.conv}%</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        ))}
      </div>

      {/* Follow-ups + Email/WA + Workload */}
      <div className="grid lg:grid-cols-3 gap-4">
        <div className="glass rounded-xl p-5">
          <h2 className="font-semibold mb-3">Follow-ups</h2>
          <div className="grid grid-cols-2 gap-3 text-sm">
            {[["Due", d.followups.due], ["Completed", d.followups.completed], ["Missed", d.followups.missed], ["Cancelled", d.followups.cancelled]].map(([l, v]) => (
              <div key={l} className="rounded-lg bg-slate-900/50 p-3"><div className="text-xl font-bold">{v}</div><div className="text-xs text-muted-foreground">{l}</div></div>
            ))}
          </div>
          {d.followups.completion_rate !== null && <p className="text-xs text-muted-foreground mt-3">Completion rate: <span className="text-emerald-400 font-semibold">{d.followups.completion_rate}%</span></p>}
        </div>

        <div className="glass rounded-xl p-5">
          <h2 className="font-semibold mb-3 flex items-center gap-2"><Mails className="h-4 w-4 text-primary" /> Communication</h2>
          <div className="space-y-2 text-sm">
            {[["Emails sent", d.email.sent], ["Emails failed", d.email.failed], ["Call CTA clicked", d.email.call_clicked], ["WhatsApp CTA clicked", d.email.whatsapp_clicked], ["WhatsApp opened", d.whatsapp.opened], ["Unsubscribed", d.email.unsubscribed]].map(([l, v]) => (
              <div key={l} className="flex justify-between"><span className="text-muted-foreground">{l}</span><span className="font-semibold">{v}</span></div>
            ))}
          </div>
        </div>

        <div className="glass rounded-xl p-5">
          <h2 className="font-semibold mb-3 flex items-center gap-2"><Users className="h-4 w-4 text-primary" /> Workload</h2>
          <div className="space-y-2 text-sm">
            {d.workload.map((w) => (
              <div key={w.id} className="flex justify-between items-center">
                <span>{w.name} {w.assignment_status === "paused" && <span className="text-[10px] text-amber-400">(paused)</span>}{w.account_status === "inactive" && <span className="text-[10px] text-red-400">(inactive)</span>}</span>
                <span className="font-semibold">{w.active}</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Recent activity */}
      <div className="glass rounded-xl p-5">
        <h2 className="font-semibold mb-3">Recent Activity</h2>
        <div className="space-y-1.5">
          {d.recent.map((r, i) => (
            <div key={i} data-testid="recent-item" className="flex justify-between text-sm py-1 border-b border-slate-800/40">
              <span className="text-muted-foreground truncate">{r.description}</span>
              <span className="text-[11px] text-slate-500 whitespace-nowrap ml-3">{new Date(r.created_at).toLocaleString()}</span>
            </div>
          ))}
        </div>
      </div>

      {/* AI foundation empty state */}
      <div className="glass rounded-xl p-5 text-center">
        <p className="text-sm text-muted-foreground">AI calling analytics will appear here once AI campaigns run. <span className="text-slate-600">No AI data yet.</span></p>
      </div>
    </div>
  );
}
